/**
 * Partner mapping. Each standard field names one source and one transform.
 * The transform set is fixed: pattern check, value map, threshold, or an
 * OneTrust group read. A new partner is a new record. A new transform is code.
 *
 * An unpublished pattern stays null. Applying that mapping reads the field
 * as unknown instead of inventing a rule. A missing source is unknown,
 * including consent. OneTrust reads its cookie in the browser. This
 * snapshot does not fill a grant when that cookie is absent. A present
 * cookie that grants none of the configured groups is unknown. Denied is
 * not a separate value until one is named. A mapping that fails its
 * checks, or a transform that cannot be applied, reads unknown instead of
 * throwing.
 */

import {
  REQUEST_CONTEXT_FIELDS,
  REQUEST_CONTEXT_UNKNOWN,
  UNKNOWN_REQUEST_CONTEXT,
  readRequestContextValue,
  type RequestContextField,
  type RequestContextKey,
  type RequestContextSnapshot,
  type RequestContextUnknown,
} from './contract';

export const REQUEST_CONTEXT_SOURCE_KINDS = ['header', 'cookie'] as const;
export type RequestContextSourceKind =
  (typeof REQUEST_CONTEXT_SOURCE_KINDS)[number];

/** A header the partner's edge sets, or a cookie the browser can change. */
export const REQUEST_CONTEXT_TRUST_LEVELS = [
  'partner-edge',
  'browser',
] as const;
export type RequestContextTrust = (typeof REQUEST_CONTEXT_TRUST_LEVELS)[number];

export const REQUEST_CONTEXT_TRANSFORM_TYPES = [
  'pattern',
  'valueMap',
  'threshold',
  'oneTrustGroup',
] as const;
export type RequestContextTransformType =
  (typeof REQUEST_CONTEXT_TRANSFORM_TYPES)[number];

export interface RequestContextSource {
  readonly kind: RequestContextSourceKind;
  readonly name: string;
}

export type RequestContextTransform =
  | {
      readonly type: 'pattern';
      /** Null when this mapping does not publish a pattern. */
      readonly pattern: string | null;
    }
  | {
      readonly type: 'valueMap';
      readonly values: Readonly<Record<string, string>>;
    }
  | {
      readonly type: 'threshold';
      readonly cutoff: number | null;
      readonly compare: 'gte' | 'lte' | null;
      readonly whenMet: string | null;
      readonly whenUnmet: string | null;
      /**
       * Second signal for a threshold. The snapshot stores the score.
       * A verified-bot header is not a snapshot field, so this stays null
       * until a threshold mapping names the token.
       */
      readonly verifiedSource: {
        readonly kind: RequestContextSourceKind;
        readonly name: string;
        readonly whenVerified: string | null;
      } | null;
    }
  | {
      readonly type: 'oneTrustGroup';
      readonly groups: readonly string[];
    };

export interface RequestContextFieldMapping {
  readonly field: RequestContextKey;
  readonly source: RequestContextSource;
  readonly trust: RequestContextTrust;
  readonly transform: RequestContextTransform;
}

export interface RequestContextClientMapping {
  readonly partnerId: string;
  readonly fields: readonly RequestContextFieldMapping[];
}

export interface RequestContextInputs {
  readonly headers?: Readonly<Record<string, unknown>>;
  readonly cookies?: Readonly<Record<string, unknown>>;
}

const FIELD_BY_KEY = new Map<RequestContextKey, RequestContextField>(
  REQUEST_CONTEXT_FIELDS.map((field) => [field.key, field]),
);

function readNamed(
  source: { readonly kind: RequestContextSourceKind; readonly name: string },
  inputs: RequestContextInputs,
): unknown {
  const bag = source.kind === 'header' ? inputs.headers : inputs.cookies;
  if (!bag || !Object.prototype.hasOwnProperty.call(bag, source.name)) {
    return undefined;
  }
  return bag[source.name];
}

function readOneTrustGroups(
  raw: unknown,
  groups: readonly string[],
): RequestContextUnknown | string {
  if (groups.length === 0) {
    return REQUEST_CONTEXT_UNKNOWN;
  }
  // A missing or blank cookie stays unknown. OneTrust reads the browser
  // cookie and applies its own startup.
  if (raw === undefined || typeof raw !== 'string' || raw.trim().length === 0) {
    return REQUEST_CONTEXT_UNKNOWN;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return REQUEST_CONTEXT_UNKNOWN;
  }

  const match = /(?:^|[&?])groups=([^&]*)/.exec(decoded);
  const groupPart = match ? match[1] : decoded;
  const consented = new Set<string>();
  for (const entry of groupPart.split(',')) {
    const separator = entry.lastIndexOf(':');
    if (separator <= 0) continue;
    const id = entry.slice(0, separator);
    const flag = entry.slice(separator + 1);
    if (flag === '1') consented.add(id);
  }

  const present = groups.filter((group) => consented.has(group));
  // A present cookie that grants none of the configured groups is unknown.
  // Denied is not a separate stored value until one is named.
  if (present.length === 0) {
    return REQUEST_CONTEXT_UNKNOWN;
  }
  return present.join(',');
}

/** A bot score is a decimal. Blank, hex, and other spellings are not scores. */
function readDecimalScore(raw: unknown): number | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
    return null;
  }
  const score = Number(trimmed);
  return Number.isFinite(score) ? score : null;
}

function applyTransform(
  transform: RequestContextTransform,
  raw: unknown,
  inputs: RequestContextInputs,
): unknown {
  try {
    if (transform.type === 'pattern') {
      if (typeof raw !== 'string') return REQUEST_CONTEXT_UNKNOWN;
      const value = raw.trim();
      if (value.length === 0) return REQUEST_CONTEXT_UNKNOWN;
      if (
        transform.pattern !== null &&
        !new RegExp(transform.pattern).test(value)
      ) {
        return REQUEST_CONTEXT_UNKNOWN;
      }
      return value;
    }

    if (transform.type === 'valueMap') {
      if (typeof raw !== 'string') return REQUEST_CONTEXT_UNKNOWN;
      const key = raw.trim();
      if (!Object.prototype.hasOwnProperty.call(transform.values, key)) {
        return REQUEST_CONTEXT_UNKNOWN;
      }
      return transform.values[key];
    }

    if (transform.type === 'threshold') {
      const verified = transform.verifiedSource;
      if (
        verified?.whenVerified &&
        transform.whenMet &&
        readNamed(verified, inputs) === verified.whenVerified
      ) {
        return transform.whenMet;
      }
      if (
        transform.cutoff === null ||
        transform.compare === null ||
        transform.whenMet === null ||
        transform.whenUnmet === null
      ) {
        return REQUEST_CONTEXT_UNKNOWN;
      }
      const score = readDecimalScore(raw);
      if (score === null) return REQUEST_CONTEXT_UNKNOWN;
      const met =
        transform.compare === 'gte'
          ? score >= transform.cutoff
          : score <= transform.cutoff;
      return met ? transform.whenMet : transform.whenUnmet;
    }

    if (transform.type === 'oneTrustGroup') {
      return readOneTrustGroups(raw, transform.groups);
    }

    return REQUEST_CONTEXT_UNKNOWN;
  } catch {
    return REQUEST_CONTEXT_UNKNOWN;
  }
}

/**
 * A cookie is browser-controlled, so it cannot be marked partner-edge trusted.
 * Returns the problems. An empty list means the mapping may be stored.
 */
export function fieldMappingIssues(
  mapping: RequestContextFieldMapping,
): readonly string[] {
  const issues: string[] = [];
  if (mapping.source.name.trim().length === 0) {
    issues.push(`${mapping.field} source name is empty`);
  }
  if (mapping.source.kind === 'cookie' && mapping.trust !== 'browser') {
    issues.push(
      `${mapping.field} is a cookie the browser can change and cannot be partner-edge trusted`,
    );
  }
  return issues;
}

/**
 * Translates one partner's headers and cookies into the five-field snapshot.
 * Fields the record does not map stay unknown. A mapping that fails
 * `fieldMappingIssues` is left unknown, including a cookie marked
 * partner-edge. A valid browser-trust cookie is still applied.
 */
export function applyRequestContextMapping(
  mapping: RequestContextClientMapping,
  inputs: RequestContextInputs,
): RequestContextSnapshot {
  const snapshot: RequestContextSnapshot = { ...UNKNOWN_REQUEST_CONTEXT };

  for (const fieldMapping of mapping.fields) {
    const field = FIELD_BY_KEY.get(fieldMapping.field);
    if (!field || fieldMappingIssues(fieldMapping).length > 0) continue;
    const raw = readNamed(fieldMapping.source, inputs);
    snapshot[field.key] = readRequestContextValue(
      applyTransform(fieldMapping.transform, raw, inputs),
      field,
    );
  }

  return snapshot;
}
